/**
 * BreadcrumbTokens - Shared Styling Tokens for Breadcrumb Components
 *
 * These tokens ensure consistent styling across all breadcrumb components:
 * - NavBreadcrumb (primary, in UnifiedHeader)
 * - BreadcrumbNav (route-based)
 * - OrganizationBreadcrumb (org hierarchy)
 *
 * Uses white/opacity for consistency with the dark theme design system.
 */

import {
  breadcrumbColorSchemes,
  getColorScheme,
  getCurrentWithColor,
  type ColorSchemeConfig,
  type NavColorScheme,
} from '@/lib/ui/colorSchemes';

// ============================================================================
// Base Tokens - Used by all breadcrumb components
// ============================================================================

export const breadcrumbTokens = {
  // Container layout
  container: 'flex items-center gap-1 text-sm',
  containerWrap: 'flex items-center flex-wrap gap-1 text-sm',

  // Home icon styling
  homeIcon: {
    base: 'p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.06] transition-all duration-200',
    icon: 'w-4 h-4',
  },

  // Separator (chevron between items)
  separator: {
    base: 'text-white/60',
    icon: 'w-3.5 h-3.5',
    // Slightly larger for BreadcrumbNav compatibility
    iconLarge: 'w-4 h-4',
  },

  // Parent items (clickable, not current)
  parent: {
    text: 'text-white/60',
    hover: 'hover:text-white',
    bg: 'hover:bg-white/[0.04]',
    transition: 'transition-colors duration-200',
    // Combined class for easy use
    link: 'text-white/60 hover:text-white hover:bg-white/[0.04] transition-colors duration-200',
    // With padding/rounded for interactive feel
    interactive: 'px-2 py-1 rounded-md text-white/60 hover:text-white hover:bg-white/[0.04] transition-all duration-200',
  },

  // Current item (last in chain, not clickable)
  current: {
    text: 'text-white font-medium',
    bg: 'bg-white/[0.06]',
    // Combined class for easy use
    base: 'text-white font-medium',
    // With background for emphasis
    emphasized: 'px-2 py-1 rounded-md text-white font-medium bg-white/[0.06]',
  },

  // Item layout (for items with icons)
  item: {
    base: 'flex items-center gap-1.5',
    icon: 'w-3.5 h-3.5',
  },

  // Dropdown styling (for NavBreadcrumb dropdown menus)
  dropdown: {
    container: 'absolute left-0 top-full pt-1 z-50',
    bridge: 'h-1', // Invisible bridge to prevent gap
    menu: 'w-56 py-1.5 bg-[#0a0a0a] rounded-lg shadow-2xl',
    item: 'flex items-center gap-3 px-3 py-2 mx-1.5 rounded-lg transition-all duration-200 group',
    itemActive: 'bg-white/[0.06]',
    itemHover: 'hover:bg-white/[0.04]',
    text: 'text-sm text-white/70 group-hover:text-white transition-colors',
    textActive: 'text-white',
    indicator: 'ml-auto w-1.5 h-1.5 rounded-full',
  },
} as const;

// ============================================================================
// Color Scheme Extensions - Re-exported from shared utilities
// ============================================================================

// breadcrumbColorSchemes is now imported from '@/lib/ui/colorSchemes'
// Re-export for backwards compatibility with existing imports
export { breadcrumbColorSchemes };

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get combined classes for a parent breadcrumb item
 */
export function getParentClasses(withInteractive = false): string {
  return withInteractive
    ? breadcrumbTokens.parent.interactive
    : `${breadcrumbTokens.parent.text} ${breadcrumbTokens.parent.hover} ${breadcrumbTokens.parent.transition}`;
}

/**
 * Get combined classes for the current breadcrumb item
 */
export function getCurrentClasses(withBackground = false): string {
  return withBackground
    ? breadcrumbTokens.current.emphasized
    : breadcrumbTokens.current.base;
}

// Re-export shared utilities for backwards compatibility
export { getColorScheme, getCurrentWithColor };

// ============================================================================
// Export
// ============================================================================

export default breadcrumbTokens;
